import {
  faArrowDownToLine,
  faBug,
  faCheckCircle,
  faCopy,
  faPen,
  faTrashCan,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";

import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { AgentVersionConfigurationOutput } from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { usePowertools } from "src/powertools/hooks";
import { useWsInfo } from "src/powertools/useWsInfo";
import { getAgentLogsInsightsLink } from "src/powertools/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { copyTextToClipboard } from "src/utils/clipboard";

type UseAgentVersionMenuOptionsParams = {
  isDraft: boolean;
  configuration?: AgentVersionConfigurationOutput;
  onEdit?: () => void;
  onDuplicate: () => void;
  onViewHistory?: () => void;
  onPublish: () => void;
  onDelete?: () => void;
  isPublishing?: boolean;
  isReadOnly?: boolean;
};

/**
 * Builds the dropdown menu options for an agent version's action menu.
 * Used by both the version detail page (PageMenu) and the version list page (AgentVersionActionsMenu).
 *
 * Regular items: Edit (draft only), Duplicate, View Agent run history (when onViewHistory is provided),
 * Publish (draft only), Delete (draft only).
 * Powertools items (separated by a divider): View Agent Stack Logs, Export config JSON.
 */
export const useAgentVersionMenuOptions = ({
  isDraft,
  configuration,
  onEdit,
  onDuplicate,
  onViewHistory,
  onPublish,
  onDelete,
  isPublishing,
  isReadOnly,
}: UseAgentVersionMenuOptionsParams): OptionsDropdownElement[] => {
  const isPowerToolsEnabled = usePowertools();
  const { workspace } = useWorkspaceContext();
  const wsInfo = useWsInfo(workspace);

  const handleOpenAgentLogs = () => {
    if (wsInfo.data?.stack_name && wsInfo.data?.region) {
      window.open(
        getAgentLogsInsightsLink(wsInfo.data.stack_name, wsInfo.data.region),
        "_blank",
      );
    }
  };

  const handleExportConfig = async () => {
    if (configuration) {
      await copyTextToClipboard(JSON.stringify(configuration, null, 2));
      toastActions.success({ title: "Configuration copied to clipboard" });
    }
  };

  const regularItems: (OptionsDropdownElement | false)[] = [
    !isReadOnly &&
      !!onEdit &&
      isDraft && {
        key: "Edit version details",
        icon: faPen,
        action: onEdit,
      },
    !isReadOnly && {
      key: "Duplicate",
      icon: faCopy,
      action: onDuplicate,
    },
    !!onViewHistory && {
      key: "View Agent run history",
      icon: faWavePulse,
      action: onViewHistory,
    },
    !isReadOnly &&
      isDraft && {
        key: "Publish",
        icon: faCheckCircle,
        action: onPublish,
        disabled: isPublishing,
      },
    !isReadOnly &&
      !!onDelete &&
      isDraft && {
        key: "Delete",
        icon: faTrashCan,
        action: onDelete,
      },
  ];

  const powertoolsItems: (OptionsDropdownElement | false)[] = [
    !!wsInfo.data?.stack_name &&
      !!wsInfo.data?.region && {
        key: "View Agent stack logs",
        icon: faBug,
        action: handleOpenAgentLogs,
      },
    !isReadOnly &&
      !!configuration && {
        key: "Export config JSON",
        icon: faArrowDownToLine,
        action: handleExportConfig,
      },
  ];

  const filteredRegular = regularItems.filter(
    (item): item is OptionsDropdownElement => !!item,
  );
  const filteredPowertools = powertoolsItems.filter(
    (item): item is OptionsDropdownElement => !!item,
  );

  if (isPowerToolsEnabled && filteredPowertools.length > 0) {
    return [...filteredRegular, MENU_DIVIDER, ...filteredPowertools];
  }

  return filteredRegular;
};
